import { HeadSection } from 'components/HeadSection';
import { useProductDetailState } from 'contexts';
import { useTenantConfig } from 'hooks';
import React from 'react';

export const MetaData: React.FC = () => {
  const { product, primitiveProduct, serverRating } = useProductDetailState();
  const { baseDomain, canonicalDomain } = useTenantConfig();

  let canonical: string | undefined;
  if (primitiveProduct?.productInfo?.canonical) {
    if (canonicalDomain) {
      canonical = `${canonicalDomain}/${primitiveProduct.productInfo.canonical}`;
    } else if (baseDomain) {
      canonical = `${baseDomain}/${primitiveProduct.productInfo.canonical}`;
    }
  }

  const data = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: primitiveProduct?.productInfo?.name,
    image: primitiveProduct?.productInfo?.imageUrl,
    brand: {
      '@type': 'Brand',
      name: primitiveProduct?.productInfo?.brand?.name,
    },
    offers: {
      '@type': 'Offer',
      price: primitiveProduct?.prices?.[0]?.latestPrice || 0,
      url: `${baseDomain}/${primitiveProduct?.productInfo?.canonical}`,
      priceCurrency: 'VND',
      itemCondition: 'https://schema.org/NewCondition',
      availability:
        primitiveProduct?.totalAvailable === null ||
        !!primitiveProduct?.totalAvailable
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
    },
    url: `${baseDomain}/${primitiveProduct?.productInfo?.canonical}`,
    description: primitiveProduct?.productDetail?.seoInfo?.metaDescription,
    sku: primitiveProduct?.productInfo?.sku,
    category: primitiveProduct?.productInfo.categories?.[0]?.name,
    additionalProperty: product?.attributeGroups
      ?.filter((att) => !!att.value && att.parentId !== 0)
      .map((att) => ({
        '@type': 'PropertyValue',
        name: att.name,
        value: att.value,
      })),
    video: product?.seoInfo?.relatedVideos?.urls?.map((url) => ({
      '@type': 'VideoObject',
      url: url.url,
      thumbnailUrl: url.thumbnailUrl,
      description: url.description,
      uploadDate: url.uploadDate,
      name: url.name,
    })),
    aggregateRating: !!serverRating?.totalReviews
      ? {
          '@type': 'AggregateRating',
          bestRating: '5',
          worstRating: '1',
          ratingValue: serverRating?.averageRating,
          reviewCount: serverRating?.totalReviews,
        }
      : undefined,
    review: !!serverRating?.reviews.length
      ? serverRating.reviews.slice(0, 5).map((review) => ({
          '@type': 'Review',
          datePublished:
            typeof review.createdAt === 'number'
              ? new Date(review.createdAt * 1000).toISOString()
              : '',
          reviewBody: review.review.comment,
          author: {
            '@type': 'Person',
            name: review.userName || 'Thành viên ẩn danh',
          },
          reviewRating: {
            '@type': 'Rating',
            bestRating: '5',
            ratingValue: review.review.rating,
          },
        }))
      : undefined,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      />
      <HeadSection
        canonical={canonical}
        title={product?.seoInfo?.metaTitle || product?.name}
        description={product?.seoInfo?.metaDescription || false}
        keywords={product?.seoInfo?.metaKeyword}
        image={product?.images?.[0]?.src}
      />
    </>
  );
};
